/**
 * FieldDeleteConfirm — modal dialog confirming soft-delete of a field.
 *
 * Props:
 *   field      — { id, name, area_ha }
 *   token      — JWT
 *   onDeleted  — () => void — called after successful soft delete
 *   onCancel   — () => void
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './overlay';
import { apiClient } from '../lib/apiClient';

export default function FieldDeleteConfirm({ field, token, onDeleted, onCancel }) {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await apiClient.delete(`/api/v1/aif/farm/fields/${field.id}`, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      onDeleted?.();
    } catch (e) {
      setError(e.body?.detail || e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal.Root open onOpenChange={(open) => { if (!open) onCancel?.(); }}>
      <Modal.Content
        size="sm"
        title={t('fieldDelete.title')}
        dismissible={false}
      >
        <Modal.Body>
          <div data-testid="field-delete-confirm" className="text-center space-y-2">
            <div className="text-3xl mb-2">🗑️</div>
            <p className="text-sm text-[var(--aif-text-muted)] leading-relaxed">
              <strong className="text-[var(--aif-text-primary)]">{field.name}</strong>
              {field.area_ha ? ` (${Number(field.area_ha).toLocaleString('de-DE', { maximumFractionDigits: 1 })} ha)` : ''}
              {' '}{t('fieldDelete.archiveHint')}
            </p>
          </div>

          {error && (
            <div className="mt-4 text-sm text-red-500 bg-red-500/10 rounded-lg px-3 py-2 text-center">
              {error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            data-testid="field-delete-cancel"
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2.5 rounded-xl border border-[var(--aif-border-default)]
                       text-[var(--aif-text-secondary)] text-sm font-medium
                       hover:bg-[var(--aif-bg-elevated)] transition disabled:opacity-50"
          >
            {t('form.cancel')}
          </button>
          <button
            data-testid="field-delete-confirm-btn"
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium
                       hover:opacity-90 transition disabled:opacity-50"
          >
            {deleting ? t('fieldDelete.deleting') : t('fieldDelete.confirm')}
          </button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
